import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { config } from '../Constants';
import { formatCurrency, getDisplayPrice } from '../utils/priceUtils';
import StatusDot from '../components/StatusDot';
import { useSiteStatus } from '../hooks/useSiteStatus';
import YouTubeIcon from '@mui/icons-material/YouTube';
import InstagramIcon from '@mui/icons-material/Instagram';
import './TopBar.css';

const URL = config.url;
const DISCORD_URL = 'https://discord.gg/W78pdPCdbC';
const TIKTOK_URL = 'https://www.tiktok.com/@stockmaid';
const YOUTUBE_URL = 'https://www.youtube.com/@StockMaid';
const INSTAGRAM_URL = 'https://www.instagram.com/stock_maid/';
const FALLBACK_MESSAGE = 'Track high-demand products & get restock alerts';

const DiscordIcon = () => (
    <svg className='top-bar-discord-icon' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
        <path d='M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z' />
    </svg>
);

const TikTokIcon = () => (
    <svg className='top-bar-tiktok-icon' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
        <path d='M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74a2.89 2.89 0 0 1 2.31-4.64a2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z' />
    </svg>
);

function TopBar() {
    const [deals, setDeals] = useState([]);
    const [dealIndex, setDealIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    // Site health beside the Status link, so nobody has to open the page to see green.
    const { summary: siteStatus } = useSiteStatus({ tick: false });

    useEffect(() => {
        const abortController = new AbortController();

        const loadDeals = async () => {
            try {
                const response = await fetch(`${URL}/api/v1/stocks/deals?page=0&size=10`, {
                    signal: abortController.signal
                });

                if (!response.ok) {
                    throw new Error(`Failed to load top bar deals (status: ${response.status})`);
                }

                const payload = await response.json();
                const list = Array.isArray(payload?.data?.content)
                    ? payload.data.content
                    : Array.isArray(payload?.data)
                        ? payload.data
                        : [];

                const normalizedDeals = list.filter((deal) => Boolean(deal?.sku)).slice(0, 10);
                setDeals(normalizedDeals);
                setDealIndex(0);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Failed to load top bar deals:', error);
                    setHasError(true);
                }
            } finally {
                setIsLoading(false);
            }
        };

        loadDeals();

        return () => {
            abortController.abort();
        };
    }, []);

    useEffect(() => {
        if (deals.length <= 1) {
            return undefined;
        }

        const interval = setInterval(() => {
            setDealIndex((previous) => (previous + 1) % deals.length);
        }, 4500);

        return () => clearInterval(interval);
    }, [deals.length]);

    const currentDeal = useMemo(() => {
        if (!deals.length) {
            return null;
        }
        return deals[dealIndex] || deals[0];
    }, [dealIndex, deals]);

    const getDealPricing = (deal) => {
        const bestBuyRegular = Number(deal?.bestBuyRegularPrice);
        const bestBuySale = Number(deal?.bestBuySalePrice);
        if (deal?.bestBuyOnSale && Number.isFinite(bestBuyRegular) && Number.isFinite(bestBuySale) && bestBuyRegular > bestBuySale) {
            return {
                price: bestBuySale,
                oldPrice: bestBuyRegular,
                savings: bestBuyRegular - bestBuySale
            };
        }

        const mcRegular = Number(deal?.mcOffer?.regularPrice);
        const mcSale = Number(deal?.mcOffer?.salePrice);
        if (deal?.mcOffer?.onSale && Number.isFinite(mcRegular) && Number.isFinite(mcSale) && mcRegular > mcSale) {
            return {
                price: mcSale,
                oldPrice: mcRegular,
                savings: mcRegular - mcSale
            };
        }

        const price = getDisplayPrice(deal);
        const regular = Number(deal?.regularPrice);
        const sale = Number(deal?.salePrice);
        if (Number.isFinite(regular) && Number.isFinite(sale) && regular > sale) {
            return {
                price,
                oldPrice: regular,
                savings: regular - sale
            };
        }

        return {
            price,
            oldPrice: null,
            savings: null
        };
    };

    const currentDealPricing = currentDeal ? getDealPricing(currentDeal) : null;
    const currentDealPriceValue = currentDealPricing?.price ?? null;
    const currentDealOldPriceValue = currentDealPricing?.oldPrice ?? null;
    const currentDealOldPrice = Number.isFinite(currentDealOldPriceValue)
        ? formatCurrency(currentDealOldPriceValue)
        : null;
    const currentDealPrice = Number.isFinite(currentDealPriceValue)
        ? formatCurrency(currentDealPriceValue)
        : null;
    const currentDealSavings = currentDealPricing?.savings ?? null;
    const hasValidDealPricing = Number.isFinite(currentDealOldPriceValue)
        && Number.isFinite(currentDealPriceValue)
        && currentDealOldPriceValue > currentDealPriceValue;
    const currentDealSavingsText = hasValidDealPricing && currentDealSavings
        ? formatCurrency(currentDealSavings)
        : null;

    return (
        <div className='top-bar' role='complementary' aria-label='Deals and utility links'>
            <div className='top-bar-inner'>
                <div className='top-bar-left'>
                    {currentDeal ? (
                        <div className='top-bar-ticker-window'>
                            <Link
                                to={`/skus/${currentDeal.sku}`}
                                className='top-bar-deal-link'
                                aria-label={`Open deal for ${currentDeal.name || `SKU ${currentDeal.sku}`}`}
                                key={`${currentDeal.sku}-${dealIndex}`}
                            >
                                <span className='top-bar-fire' aria-hidden='true'>🔥</span>
                                <span className='top-bar-deal-name'>{currentDeal.name || `SKU ${currentDeal.sku}`}</span>
                                {hasValidDealPricing && currentDealOldPrice && (
                                    <span className='top-bar-old-price'>{currentDealOldPrice}</span>
                                )}
                                {currentDealPrice && (
                                    <span className='top-bar-deal-price'>{currentDealPrice}</span>
                                )}
                                {currentDealSavingsText && (
                                    <span className='top-bar-savings'>Save {currentDealSavingsText}</span>
                                )}
                            </Link>
                        </div>
                    ) : (
                        !isLoading && (
                            <Link
                                to='/stocks'
                                className='top-bar-deal-link'
                                aria-label={hasError ? 'Deals unavailable, browse tracked products' : 'Browse tracked products'}
                            >
                                <span className='top-bar-fallback'>{FALLBACK_MESSAGE}</span>
                            </Link>
                        )
                    )}
                </div>

                <div className='top-bar-right'>
                    <Link to='/blog' className='top-bar-link'>
                        Blog
                    </Link>
                    <Link
                        to='/status'
                        className='top-bar-link'
                        aria-label={siteStatus ? `Status: ${siteStatus.label}` : 'Status'}
                        title={siteStatus ? siteStatus.label : undefined}
                    >
                        {siteStatus && <StatusDot level={siteStatus.level} />}
                        <span>Status</span>
                    </Link>
                    {/* A page, not mailto: on a phone mailto opens the Mail app's Add Account screen (#402). */}
                    <Link to='/support' className='top-bar-link'>
                        Support
                    </Link>
                    <a
                        href={DISCORD_URL}
                        className='top-bar-link top-bar-discord-link'
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        <DiscordIcon />
                        <span>Join Discord</span>
                    </a>
                    <span className='top-bar-socials'>
                        <a
                            href={TIKTOK_URL}
                            className='top-bar-link top-bar-social-link'
                            target='_blank'
                            rel='noopener noreferrer'
                            aria-label='StockMaid on TikTok'
                            title='TikTok'
                        >
                            <TikTokIcon />
                        </a>
                        <a
                            href={YOUTUBE_URL}
                            className='top-bar-link top-bar-social-link'
                            target='_blank'
                            rel='noopener noreferrer'
                            aria-label='StockMaid on YouTube'
                            title='YouTube'
                        >
                            <YouTubeIcon className='top-bar-youtube-icon' aria-hidden='true' />
                        </a>
                        <a
                            href={INSTAGRAM_URL}
                            className='top-bar-link top-bar-social-link'
                            target='_blank'
                            rel='noopener noreferrer'
                            aria-label='StockMaid on Instagram'
                            title='Instagram'
                        >
                            <InstagramIcon className='top-bar-instagram-icon' aria-hidden='true' />
                        </a>
                    </span>
                </div>
            </div>
        </div>
    );
}

export default TopBar;
